import { useContext, useEffect } from 'react'
import styled from 'styled-components'

import type { operations } from '../../constants/schema'

import { StoresContext } from '../../provider/StoreProvider'

import { oAuthSignIn, localLogin } from '../../api/auth'

import { Head } from '../../components/head/Head'
import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { Text } from '../../components/text'
import { Header } from '../../components/header'

import { Color } from '../../theme/color'

import { ReactComponent as AppleIcon } from '../../assets/logo/sns/apple/apple_signin.svg'
import GoogleIcon from '../../assets/logo/sns/google/google_20@3x.png'
import KakaoIcon from '../../assets/logo/sns/kakao/kakao_20@3x.png'
import NaverIcon from '../../assets/logo/sns/naver/naver_20@3x.png'
import Yes24Icon from '../../assets/logo/sns/yes24/yes24_20@3x.png'

type Provider = operations['AuthController_webUrl']['parameters']['query']['provider']

const HEAD_INFO = {
  title: '로그인 | 미술품 조각투자 플랫폼, 아티피오',
  description:
    '예스24가 만든 미술품 조각투자 플랫폼 아티피오에서 블루칩 작가의 작품을 함께 소유하고 청약을 통해 나의 자산으로 만들어보세요.',
  ogTitle: '로그인 | 미술품 조각투자 플랫폼, 아티피오',
  ogDescription:
    '예스24가 만든 미술품 조각투자 플랫폼 아티피오에서 블루칩 작가의 작품을 함께 소유하고 청약을 통해 나의 자산으로 만들어보세요.',
  ogUrl: window.location.href,
}

export function SignIn() {
  const { commonStore } = useContext(StoresContext)

  const lastLoginProvider = localStorage.getItem('lastLoginProvider') as Provider

  useEffect(() => {
    commonStore.setShowTab(false)
  }, [])

  const onSignIn = async (provider: Provider) => {
    // console.log({ provider })
    // await localLogin(2)
    const redirectPath = new URLSearchParams(location.search)
    await oAuthSignIn({ provider, redirectPath: redirectPath.get('redirectPath') || undefined })
  }

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header type="SUB" title="로그인" />
        <Content>
          <SignInContainer>
            <TitleContainer>
              <Text size={26} lineHeight={38}>
                투자도 예술이 된다.
              </Text>
              <Text size={26} weight={700} lineHeight={38}>
                예스24의 아트테크 플랫폼,
                <br />
                아티피오
              </Text>
              <Text size={16} lineHeight={23} color="text_secondary">
                원하는 SNS 계정으로 시작해 보세요
              </Text>
            </TitleContainer>
            <BtnContainer type="YES24" isLastLogin={lastLoginProvider === 'yes24'} onClick={() => onSignIn('yes24')}>
              <SnsLoginIcon domain="yes24" />
              <Text size={16} weight={500} color="text_white">
                YES24로 로그인
              </Text>
            </BtnContainer>
            <BtnContainer type="NAVER" isLastLogin={lastLoginProvider === 'naver'} onClick={() => onSignIn('naver')}>
              <SnsLoginIcon domain="naver" />
              <Text size={16} weight={500} color="text_white">
                네이버로 로그인
              </Text>
            </BtnContainer>
            <BtnContainer type="KAKAO" isLastLogin={lastLoginProvider === 'kakao'} onClick={() => onSignIn('kakao')}>
              <SnsLoginIcon domain="kakao" />
              <Text size={16} weight={500}>
                카카오로 로그인
              </Text>
            </BtnContainer>
            <BtnContainer type="GOOGLE" isLastLogin={lastLoginProvider === 'google'} onClick={() => onSignIn('google')}>
              <SnsLoginIcon domain="google" />
              <Text size={16} weight={500}>
                구글로 로그인
              </Text>
            </BtnContainer>
            <BtnContainer type="APPLE" isLastLogin={lastLoginProvider === 'apple'} onClick={() => onSignIn('apple')}>
              <SnsLoginIcon domain="apple" />
              <Text size={16} weight={500} color="text_white">
                Apple로 로그인
              </Text>
            </BtnContainer>
          </SignInContainer>
        </Content>
      </Wrap>
    </>
  )
}

function SnsLoginIcon({ domain }: { domain: 'apple' | 'google' | 'kakao' | 'naver' | 'yes24' }) {
  switch (domain) {
    case 'apple':
      return <AppleIcon />
    case 'google':
      return <SnsLogoImg src={GoogleIcon} alt="Google Logo" />
    case 'kakao':
      return <SnsLogoImg src={KakaoIcon} alt="Kakao Logo" />
    case 'naver':
      return <SnsLogoImg src={NaverIcon} alt="Naver Logo" />
    case 'yes24':
      return <SnsLogoImg src={Yes24Icon} alt="Yes24 Logo" />
  }
}

const SignInContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  padding: 16px;
`

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 64px 0 81px;
  padding-left: 8px;
  > *:last-child {
    margin-top: 10px;
  }
`

const BtnContainer = styled.div<{ type: 'NAVER' | 'KAKAO' | 'GOOGLE' | 'YES24' | 'APPLE'; isLastLogin: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  min-height: 48px;
  padding: 12px 0;
  background-color: ${({ type }) =>
    type === 'NAVER'
      ? '#1ec800'
      : type === 'KAKAO'
        ? '#F9E000'
        : type === 'YES24'
          ? '#0080FF'
          : type === 'GOOGLE'
            ? '#FFFFFF'
            : type === 'APPLE'
              ? '#000000'
              : null};
  ${({ type }) => (type === 'GOOGLE' ? `border: solid 1px ${Color.gray_4}` : null)};
  border-radius: 8px;
  cursor: pointer;
  ${({ isLastLogin }) =>
    isLastLogin
      ? `
    &::before {
      display: block;
      content: '';
      position: absolute;
      bottom: 0;
      width: 8px;
      height: 8px;
      background-color: #ffffff;
      transform: rotateZ(45deg);
    }
    &::after {
      display: block;
      content: '마지막으로 로그인 했어요!';
      position: absolute;
      bottom: 0;
      left: 50%;
      z-index: 1;
      padding: 8px;
      border-radius: 8px;
      background-color: #ffffff;
      box-shadow:  0 5px 5px #999999;
      font-size: 12px;
      transform: translate(-50%, calc(100% - 4px));
    }
  `
      : ''}
`

const SnsLogoImg = styled.img`
  width: 20px;
  height: 20px;
`
